/**
 * WebSocket provider for the mobile app.
 *
 * Uses a module-level singleton WebSocket connection — survives React
 * StrictMode double-mounts and component re-renders.
 *
 * The WebSocketProvider just bridges the singleton into React context.
 * Components use useWebSocketEvent() for screen-scoped listeners.
 */

import type { RealtimeEventMap, RealtimeEventType } from "@taskhealth/shared";
import Constants from "expo-constants";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { AppState, Platform } from "react-native";

import { getValidToken } from "./api";
import { getApiBase, getWsUrl } from "./api-env";
import { chooseWsAuth, createWsAuthPolicy, fetchWsTicket, type WsAuthKind } from "./ws-auth";

// Sent with the heartbeat so the admin panel can show which platform and build
// a nurse was last on. Costs nothing: it rides the ping that already fires.
const APP_VERSION = Constants.expoConfig?.version ?? "unknown";
const APP_PLATFORM = Platform.OS === "ios" || Platform.OS === "android" ? Platform.OS : "web";
const MAX_RECONNECT_DELAY = 30000;
const MAX_RETRIES = 10;
// App-level heartbeat. React Native's WebSocket doesn't expose protocol-level
// ping/pong, and mobile sockets die silently (NAT idle timeouts, Wi-Fi↔LTE
// handoffs) without ever firing `onclose`. We send `{ action: "ping" }` — which
// both the API Gateway $default Lambda and the local dev server answer with
// `{ type: "pong" }` — and force a reconnect if no traffic comes back in time.
const HEARTBEAT_INTERVAL = 25000;
const PONG_TIMEOUT = 10000;

type EventListener = (data: any) => void;

// ─── Module-level singleton ──────────────────────────
// Lives outside React — immune to StrictMode double-mount

const listeners = new Map<RealtimeEventType, Set<EventListener>>();
// Fired whenever the socket *re*-establishes (not the first connect of the
// session). Screens use it to catch up on messages that arrived while the
// socket was down — those events were never delivered live.
const reconnectListeners = new Set<() => void>();
let ws: WebSocket | null = null;
let retryCount = 0;
let retryTimeout: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let pongTimer: ReturnType<typeof setTimeout> | null = null;
let connected = false;
let hasConnectedOnce = false;
let onStatusChange: ((connected: boolean) => void) | null = null;
// Ticket or token for the next connect, with fallback; see ws-auth.ts.
const authPolicy = createWsAuthPolicy();
// Bumped by every connect() that goes on to open a socket and by disconnect(),
// so a connect still waiting on its token or ticket can tell it was overtaken
// (a newer connect, or the app went to the background) and stand down instead
// of opening a second socket.
let connectGeneration = 0;

function clearHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
    if (pongTimer) {
        clearTimeout(pongTimer);
        pongTimer = null;
    }
}

// Treat a socket that stopped answering pings as dead: tear it down and
// reconnect with the same backoff policy as `onclose`. `onclose` may never
// fire on a half-open connection, which is exactly the case we're rescuing.
function handleDeadSocket(socket: WebSocket) {
    if (ws !== socket) {
        return;
    }
    console.warn("[WS] Heartbeat timed out — connection is dead, reconnecting");
    clearHeartbeat();
    socket.onclose = null;
    try {
        socket.close();
    } catch {
        // Already closing/closed
    }
    ws = null;
    connected = false;
    onStatusChange?.(false);
    if (retryCount < MAX_RETRIES) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), MAX_RECONNECT_DELAY);
        retryCount++;
        if (retryTimeout) {
            clearTimeout(retryTimeout);
        }
        retryTimeout = setTimeout(connect, delay);
    }
}

function startHeartbeat(socket: WebSocket) {
    clearHeartbeat();
    heartbeatTimer = setInterval(() => {
        if (ws !== socket || socket.readyState !== WebSocket.OPEN) {
            return;
        }
        try {
            socket.send(
                JSON.stringify({
                    action: "ping",
                    platform: APP_PLATFORM,
                    appVersion: APP_VERSION
                })
            );
        } catch {
            handleDeadSocket(socket);
            return;
        }
        // Any inbound traffic (the pong, or a real event) clears this in
        // `onmessage`. If nothing arrives, the socket is dead.
        if (pongTimer) {
            clearTimeout(pongTimer);
        }
        pongTimer = setTimeout(() => handleDeadSocket(socket), PONG_TIMEOUT);
    }, HEARTBEAT_INTERVAL);
}

function notifyReconnect() {
    for (const fn of reconnectListeners) {
        fn();
    }
}

function socketBusy(): boolean {
    return !!ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING);
}

async function connect() {
    // Already connected or connecting
    if (socketBusy()) {
        return;
    }
    const generation = ++connectGeneration;
    const token = await getValidToken();
    if (generation !== connectGeneration) {
        return;
    }
    if (!token) {
        retryTimeout = setTimeout(connect, 1000);
        return;
    }
    if (socketBusy()) {
        return;
    }

    // One-time ticket instead of the JWT in the URL; falls back to the token
    // whenever the ticket cannot be had. Never fails the connect by itself.
    const auth = await chooseWsAuth(authPolicy, token, (t) =>
        fetchWsTicket(getApiBase(), t, fetch)
    );
    if (generation !== connectGeneration || socketBusy()) {
        return;
    }
    const authKind: WsAuthKind = auth.kind;
    let opened = false;

    const socket = new WebSocket(
        `${getWsUrl()}?${auth.query}&platform=${APP_PLATFORM}` +
            `&appVersion=${encodeURIComponent(APP_VERSION)}`
    );

    socket.onopen = () => {
        opened = true;
        authPolicy.opened(authKind);
        retryCount = 0;
        connected = true;
        onStatusChange?.(true);
        startHeartbeat(socket);
        // Don't refetch on the very first connect — screens already load on
        // mount. Every later (re)connect may have missed live events, so tell
        // screens to catch up.
        if (hasConnectedOnce) {
            notifyReconnect();
        }
        hasConnectedOnce = true;
        console.warn("[WS] Connected to", getWsUrl());
    };

    socket.onmessage = (event) => {
        // Any inbound traffic proves the socket is alive — reset the liveness
        // timer the heartbeat is waiting on.
        if (pongTimer) {
            clearTimeout(pongTimer);
            pongTimer = null;
        }
        try {
            const msg = JSON.parse(event.data);
            if (msg.type === "pong") {
                return;
            }
            console.warn("[WS] Message received:", msg.type, msg);
            if (msg.type && msg.data) {
                const set = listeners.get(msg.type as RealtimeEventType);
                if (set) {
                    for (const fn of set) {
                        fn(msg.data);
                    }
                }
            }
        } catch {
            // Ignore malformed
        }
    };

    socket.onclose = () => {
        console.warn("[WS] Disconnected");
        if (!opened) {
            // Refused or unreachable before it opened: with a ticket, the next
            // attempt uses the token (ws-auth.ts).
            authPolicy.failedBeforeOpen(authKind);
        }
        clearHeartbeat();
        connected = false;
        onStatusChange?.(false);
        // Only reconnect if this is still our active socket
        if (ws === socket && retryCount < MAX_RETRIES) {
            ws = null;
            const delay = Math.min(1000 * Math.pow(2, retryCount), MAX_RECONNECT_DELAY);
            retryCount++;
            retryTimeout = setTimeout(connect, delay);
        }
    };

    socket.onerror = () => {};

    ws = socket;
}

/**
 * Tell the server whether this web tab is actually being looked at.
 *
 * Native closes its socket on background, so an open socket already means "app
 * in the foreground". Web deliberately keeps the socket alive on a hidden tab
 * (WEBSOCKET_GUIDE rule 9) so realtime events still land — which would leave a
 * forgotten tab reporting the nurse as online indefinitely. This is the
 * cheapest fix: one message on an existing socket, no new endpoint, no UI.
 */
function sendVisibility(state: "away" | "active") {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
    }
    try {
        ws.send(
            JSON.stringify({
                action: state,
                platform: APP_PLATFORM,
                appVersion: APP_VERSION
            })
        );
    } catch {
        // Socket died between the check and the send; the heartbeat will
        // notice and reconnect.
    }
}

function disconnect() {
    // A connect still waiting on its token or ticket must not open a socket
    // after this (e.g. the app just went to the background).
    connectGeneration++;
    clearHeartbeat();
    if (retryTimeout) {
        clearTimeout(retryTimeout);
        retryTimeout = null;
    }
    if (ws) {
        ws.onclose = null;
        ws.close();
        ws = null;
    }
    connected = false;
    retryCount = 0;
    onStatusChange?.(false);
}

function addListener(type: RealtimeEventType, fn: EventListener): () => void {
    if (!listeners.has(type)) {
        listeners.set(type, new Set());
    }
    listeners.get(type)!.add(fn);
    return () => {
        const set = listeners.get(type);
        if (set) {
            set.delete(fn);
            if (set.size === 0) {
                listeners.delete(type);
            }
        }
    };
}

// ─── React context (thin bridge) ─────────────────────

interface WebSocketContextValue {
    isConnected: boolean;
    addEventListener: (type: RealtimeEventType, listener: EventListener) => () => void;
}

const WebSocketContext = createContext<WebSocketContextValue>({
    isConnected: false,
    addEventListener: () => () => {}
});

export function useWebSocket(): WebSocketContextValue {
    return useContext(WebSocketContext);
}

export function useWebSocketEvent<T extends RealtimeEventType>(
    type: T,
    callback: (data: Extract<RealtimeEventMap, { type: T }>["data"]) => void
): void {
    const { addEventListener } = useWebSocket();
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        return addEventListener(type, (data) => callbackRef.current(data));
    }, [type, addEventListener]);
}

/**
 * Runs `callback` whenever the WebSocket *re*-establishes a connection (after a
 * drop, a heartbeat-detected dead socket, or returning from background — but not
 * the first connect of the session). Use it to refetch data that may have
 * changed via events that were never delivered live while the socket was down.
 */
export function useWebSocketReconnect(callback: () => void): void {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const fn = () => callbackRef.current();
        reconnectListeners.add(fn);
        return () => {
            reconnectListeners.delete(fn);
        };
    }, []);
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
    const [isConnected, setIsConnected] = useState(connected);

    // Bridge module-level status into React state
    useEffect(() => {
        onStatusChange = setIsConnected;
        // Start connection (no-op if already connected)
        connect();
        return () => {
            onStatusChange = null;
        };
    }, []);

    // AppState — pause on background, resume on foreground
    // Skip on web — AppState fires spurious events in Expo Web
    useEffect(() => {
        if (typeof document !== "undefined") {
            // Web: use visibilitychange instead
            const handler = () => {
                if (document.visibilityState === "visible") {
                    connect();
                    // Presence: the socket stays open on a hidden tab (see
                    // below), so the server cannot tell "looking at the app"
                    // from "tab left open three days ago" without being told.
                    sendVisibility("active");
                } else {
                    sendVisibility("away");
                }
                // Don't disconnect on hidden — web tabs should stay connected
            };
            document.addEventListener("visibilitychange", handler);
            return () => document.removeEventListener("visibilitychange", handler);
        }
        // Native: use AppState
        const sub = AppState.addEventListener("change", (state) => {
            if (state === "active") {
                connect();
            } else {
                disconnect();
            }
        });
        return () => sub.remove();
    }, []);

    const value = React.useMemo(
        () => ({ isConnected, addEventListener: addListener }),
        [isConnected]
    );

    return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}
